import React, { createContext, useContext, useReducer, useEffect } from "react";
import apiService from "../services/apiService";

/**
 * Authentication Context for Farm Monitor Admin Panel
 *
 * Provides authentication state management across the application.
 * Handles login, logout, and user session management.
 */

const AuthContext = createContext();

// Auth action types
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  SET_USER: "SET_USER",
  SET_LOADING: "SET_LOADING",
};

// Initial auth state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload.loading,
      };

    default:
      return state;
  }
}

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing auth on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const token = apiService.getAuthToken();
      const user = apiService.getCurrentUser();
      const adminProfile = apiService.getCurrentAdminProfile();

      console.log("Auth check - Token:", token ? "Present" : "Missing");
      console.log("Auth check - User:", user);
      console.log("Auth check - Admin Profile:", adminProfile);

      if (token && user) {
        // Verify user has admin, super_admin, or farms_owner role
        if (
          user.role === "admin" ||
          user.role === "super_admin" ||
          user.role === "farms_owner"
        ) {
          console.log("User authenticated as admin");

          // Merge admin profile data (including farms) with user data
          const enhancedUser = {
            ...user,
            farms: adminProfile?.farms || [],
          };

          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: { user: enhancedUser, token },
          });
        } else {
          console.log("User is not admin, clearing auth");
          // User is not admin, clear auth
          apiService.clearAuthToken();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else if (token) {
        // We have token but no user data, try to create user from token
        console.log(
          "Token exists but no user data, attempting to reconstruct user",
        );
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (
            payload.role === "admin" ||
            payload.role === "super_admin" ||
            payload.role === "farms_owner"
          ) {
            const reconstructedUser = {
              id: payload.user_id,
              email: payload.email,
              role: payload.role,
            };
            localStorage.setItem(
              "admin_user",
              JSON.stringify(reconstructedUser),
            );
            console.log("User reconstructed from token:", reconstructedUser);
            dispatch({
              type: AUTH_ACTIONS.SET_USER,
              payload: { user: reconstructedUser, token },
            });
          } else {
            apiService.clearAuthToken();
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          }
        } catch (tokenError) {
          console.error("Failed to parse token:", tokenError);
          apiService.clearAuthToken();
          dispatch({
            type: AUTH_ACTIONS.SET_LOADING,
            payload: { loading: false },
          });
        }
      } else {
        console.log("No token or user found");
        dispatch({
          type: AUTH_ACTIONS.SET_LOADING,
          payload: { loading: false },
        });
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: { loading: false } });
    }
  };

  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await apiService.login(credentials);

      if (response.success) {
        const { user, admin, access_token } = response.data;

        // Check if user has admin, super_admin, or farms_owner role
        if (
          user.role !== "admin" &&
          user.role !== "super_admin" &&
          user.role !== "farms_owner"
        ) {
          throw new Error("Access denied. Admin privileges required.");
        }

        // Merge admin profile data (including farms) with user data
        const enhancedUser = {
          ...user,
          farms: admin?.farms || [],
        };

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: enhancedUser, token: access_token },
        });

        // Handle redirect after login if stored
        const redirectPath = localStorage.getItem("redirectAfterLogin");
        if (redirectPath) {
          localStorage.removeItem("redirectAfterLogin");
          // Use setTimeout to ensure the login success is processed first
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 10000);
        }

        return { success: true, user: enhancedUser };
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (error) {
      const errorMessage = error.message || "Login failed. Please try again.";
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage },
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    } catch (error) {
      console.error("Logout error:", error);
      // Force logout even if API call fails
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  const updateUser = (user) => {
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: { user, token: state.token },
    });
  };

  const contextValue = {
    ...state,
    login,
    logout,
    updateUser,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
